import { useCallback, useContext, useEffect, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import * as yup from "yup";

import AuthenticationContext from "../../shared/AuthenticationContext";
import ErrorContext from "../../shared/ErrorContext";
import { TranslationService } from "../../../services/TranslationService";
import AuthenticationService, {
  ValidLogonResponse
} from "../../../services/AuthenticationService";
import ModalService from "../../shared/bootstrap/modal/Modal";
import {
  build2AFUrl,
  buildPasswordExpiredUrl,
  doCompanyLogin,
  routeByVersion
} from "../../../utils/authHelpers";
import FloatingPanelService from "../../shared/FloatingPanel";

export const getRememberCacheValue = () => {
  let dataResponse = { rememberUser: false, userEmailCache: "" };
  const keys = Object.keys(localStorage);

  for (const key of keys) {
    if (key.startsWith("company_cache_user_prefs_")) {
      const data = localStorage.getItem(key);

      if (data !== null) {
        const parsedData = JSON.parse(data);
        const rememberUser = parsedData.rememberUser;
        const userEmailCache = parsedData.userEmailCache;

        dataResponse.rememberUser = rememberUser;
        dataResponse.userEmailCache = userEmailCache;
      } else {
        dataResponse.rememberUser = false;
        dataResponse.userEmailCache = "";
      }
    }
  }
  return dataResponse;
};

/** Estado y lógica de autenticación compartidos por Logon y LogonV2. */
export const useLogonForm = () => {
  const { doLogout } = useContext(AuthenticationContext);
  const { rememberUser, userEmailCache } = getRememberCacheValue();

  const [formData, setFormData] = useState({
    email: userEmailCache,
    password: "",
    remember: rememberUser
  });
  const [loading, setLoading] = useState(false);
  const [visiblePassword, setVisiblePassword] = useState(false);

  const { translate, currentLanguage } = TranslationService;
  const { setCurrentError } = useContext(ErrorContext);
  const { setCurrentAuthentication } = useContext(AuthenticationContext);

  const history = useHistory();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const msg = searchParams.get("msg") ?? "";
  const msgtr = searchParams.get("msgtr") ?? "";

  const search = window.location.search;
  const match = search.match(/returnUrl=(.*)/);
  const returnUrl = match ? decodeURIComponent(match[1]) : "";

  const hasMessage = msg.length > 0 || msgtr.length > 0;
  const messageContent = (translate as any)[msgtr] || msg;

  useEffect(() => {
    ModalService.hideModal();
    FloatingPanelService.hidePanel();
  }, []);

  const formDataSchema = yup.object({
    email: yup
      .string()
      .email(translate.LoginEmailIncorrect)
      .required(translate.LoginEmailMissing),
    password: yup.string().required(translate.LoginPasswordMissing)
  });

  async function handleLogIn(): Promise<void> {
    setCurrentError(undefined);

    if (!formData.email && !formData.password) {
      setCurrentError(translate.LoginIncorrect);
      return;
    }

    try {
      await formDataSchema.validate(formData);

      setLoading(true);
      const userData = await AuthenticationService.login(
        formData.email,
        formData.password
      );

      if (userData instanceof Error) {
        setCurrentError(translate.Network);
        return;
      }

      switch (userData.response) {
        case "LoginIncorrect":
          setCurrentError(translate.LoginIncorrect);
          return;

        case "NextBlock":
          setCurrentError(translate.NextBlock);
          return;

        case "2AF": {
          const url = build2AFUrl(
            currentLanguage,
            location.search,
            userData.twofadata,
            formData.email,
            formData.remember
          );
          history.replace(url);
          return;
        }

        case "passwordexpired": {
          const url = buildPasswordExpiredUrl(
            currentLanguage,
            location.search,
            userData.expiredtoken,
            formData.email
          );
          history.replace(url);
          return;
        }

        case "OK":
          await companyLogin(userData, returnUrl);
          return;

        case "Blocked":
          setCurrentError(translate.Blocked);
          return;

        default:
          setCurrentError(translate.Network);
          return;
      }
    } catch (err: any) {
      setCurrentError(err?.errors?.[0] ?? translate.Network);
    } finally {
      setLoading(false);
    }
  }

  const companyLogin = useCallback(
    async (userData: ValidLogonResponse, returnUrl?: string) => {
      try {
        await doCompanyLogin({
          userData,
          remember: formData.remember,
          setCurrentAuthentication,
          cacheEmail: formData.email
        });

        await routeByVersion({
          history,
          location: window.location,
          doLogout,
          currentLanguage,
          overrideReturnUrl: returnUrl,
          reloadOnInternal: true
        });
      } catch {
        setCurrentError(translate.Network);
      }
    },
    [
      formData.remember,
      formData.email,
      setCurrentAuthentication,
      setCurrentError,
      translate.Network,
      currentLanguage,
      history,
      doLogout
    ]
  );

  const handleVisiblePassword = () =>
    setVisiblePassword((prevState) => !prevState);

  const handleInputChange = ({
    target: { name, value, checked, type }
  }: React.ChangeEvent<HTMLInputElement>) => {
    setFormData((prevState) => ({
      ...prevState,
      [name]: type === "checkbox" ? checked : value
    }));
  };

  return {
    formData,
    loading,
    visiblePassword,
    hasMessage,
    messageContent,
    handleLogIn,
    handleVisiblePassword,
    handleInputChange
  };
};
